import { computed, DestroyRef, inject, Injectable, linkedSignal, signal } from "@angular/core";
import { AddressSearchApiService } from "src/app/shared/services/address-search-api.service";
import { SuggestionAddress } from "src/app/shared/contract/Address/AddressSearchResponse";
import { Coordinate, ObjectCoordinate } from "src/app/shared/contract/Address/Coordinates";
import { EPSGType } from "src/app/shared/contract/Enum/EPSG";
import { MatchAccurence } from "src/app/shared/contract/Enum/MatchAccurence";
import { MapViewService } from "src/app/shared/services/map-view.service";
import { DataModeService } from "src/app/modules/menu/data-mode.service";
import { Router } from "@angular/router";
import { AddressMarkerService } from "./address-marker.service";
import { Address } from "../contract/Address/Address";
import { MenuService } from "src/app/modules/menu/menu.service";
import { ObjectInformationService } from "src/app/modules/object-information/object-information.service";
import { CacheService } from "src/app/modules/cache/cache.service";
import { AccumateModule, MODULE_NAMES } from "../consts/icon-features";
import { InterfaceStatusNotificationService } from "src/app/shared/services/interface-status-notification.service";
import { InterfaceStatus } from "../contract/Enum/Status";
import { PermissionService } from "src/app/modules/core/services/permission.service";
import { InitService } from "src/app/shared/services/init.service";
import { catchError, filter, finalize, map, of, switchMap, tap } from "rxjs";
import { DuplicateCheckService } from "./duplicate-check.service";
import { EstateType } from "../contract/Enum/portfolio.enum";
import { BasemapService } from "../../modules/basemap/basemap.service";
import { PlotService } from "src/app/modules/plot/services/plot.service";
import { PlotRequest } from "../contract/Plot/PlotRequest";
import { LookupResponse } from "../contract/Address/lookup-response";
import { TinyObject } from "../contract/Address/tiny-object";
import { TinyPlot } from "../contract/Address/tiny-plot";
import { takeUntilDestroyed, toSignal } from "@angular/core/rxjs-interop";
import { AddressSearchRequest } from "../contract/Address/address-search-request";
import { LoadingService } from "src/app/modules/loading/loading.service";
import { getObjectCoordinateFromCoordinate } from "../helpers/coordinate-helper";
import { RealEstateStoreService } from "../../modules/real-estate/services/real-estate-store.service";
import { ThematicInfoService } from "@services/real-estate/thematic-info/thematic-info.service";
import { RealEstateInitService } from "@services/real-estate/real-estate-init.service";
import { RealEstateService } from "@services/real-estate/real-estate.service";
import { RvmApiService } from "src/app/modules/real-estate/inherit-real-estate-api-services/rvm-api.service";
import { MixedRealEstateService } from "src/app/modules/real-estate/inherit-real-estate-service/mixed-real-estate.service";
import { FullObjectInformationRequest } from "../contract/Address/full-object-information-request";
import { CapitalizedIncomeValueService } from "src/app/modules/capitalized-income-value/capitalized-income-value.service";

@Injectable()
export class HeaderLocationService {
  private readonly dataModeService = inject(DataModeService);
  private readonly addressSearchService = inject(AddressSearchApiService);
  private readonly addressMarkerService = inject(AddressMarkerService);
  private readonly mapViewService = inject(MapViewService);
  private readonly basemapService = inject(BasemapService);
  private readonly router = inject(Router);
  private readonly menuService = inject(MenuService);

  private readonly realEstateStoreService = inject(RealEstateStoreService);
  private readonly rvmApiService = inject(RvmApiService);
  private readonly mixedRealEstateService = inject(MixedRealEstateService);
  private readonly thematicInformationService = inject(ThematicInfoService);
  private readonly realEstateInitService = inject(RealEstateInitService);
  private readonly oldRealEstateService = inject(RealEstateService);
  private readonly plotService = inject(PlotService);

  private readonly objectInformationService = inject(ObjectInformationService);
  private readonly cacheService = inject(CacheService);
  private readonly interfaceStatusNotificationService = inject(InterfaceStatusNotificationService);
  private readonly permissionService = inject(PermissionService);
  private readonly initService = inject(InitService);
  private readonly duplicateCheckService = inject(DuplicateCheckService);
  private readonly loadingService = inject(LoadingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly capitalizedIncomeValueService = inject(CapitalizedIncomeValueService);
  private readonly initData = toSignal(this.initService.initData$, { initialValue: this.initService.InitData });

  address = signal("");

  suggestions = signal<SuggestionAddress[]>([]);
  selectedSuggestion = signal<SuggestionAddress | null>(null);
  showSuggestions = signal(false);

  lookupResponse = signal<LookupResponse>(null);
  showAddressLookup = signal(false);
  isLoadingLookup = signal(false);
  hasLookupError = signal<boolean | null>(false);

  readonly canUseGlobalAddressSearch = computed(() => !!this.initData()?.globalAddressSearch);
  readonly isGlobalSearchMode = linkedSignal<boolean, boolean>({
    source: this.canUseGlobalAddressSearch,
    computation: (canUseGlobalAddressSearch, previous) => (canUseGlobalAddressSearch ? (previous?.value ?? false) : false)
  });

  toggleGlobalSearchMode() {
    if (!this.canUseGlobalAddressSearch()) {
      this.isGlobalSearchMode.set(false);
      return;
    }

    this.isGlobalSearchMode.update(value => !value);
  }

  searchAddress(addressSearchRequest: AddressSearchRequest) {
    if (this.dataModeService?.selectedDataMode()?.module === MODULE_NAMES.Portfolio) {
      this.goToHome();
    }

    this.resetSuggestions();

    if (!addressSearchRequest?.searchTerm?.trim?.()?.length) {
      this.reset();
      return;
    }

    const searchTerm = addressSearchRequest.searchTerm.trim();
    this.addressSearchService.getAddress({ ...addressSearchRequest, searchTerm }).subscribe(response => {
      this.suggestions.set(response.addresses);
      this.showSuggestions.set(true);
    });
  }

  lookupAddress(address: SuggestionAddress) {
    // Show description in the search input
    if (address) {
      this.address.set(address.description);
    }

    // Reset error state and set loading state to true when starting the lookup
    this.hasLookupError.set(false);
    this.isLoadingLookup.set(true);

    this.addressSearchService.getLookupInfo(address?.address).subscribe({
      next: response => {
        this.lookupResponse.set(response);
        this.isLoadingLookup.set(false);
        this.hasLookupError.set(false);
      },
      error: error => {
        console.error("lookup error", error);
        this.isLoadingLookup.set(false);
        this.hasLookupError.set(true);
        this.lookupResponse.set(null);
      }
    });
  }

  selectAddress(suggestionAddress: SuggestionAddress) {
    const countryRightsModuleIds = [MODULE_NAMES.CommercialRealEstate, MODULE_NAMES.ResidentialRealEstate, MODULE_NAMES.Rvm];

    // Get module IDs for the modules we need to check
    const moduleIds = countryRightsModuleIds
      .map(moduleName => this.initService.InitData.moduleRight.find(e => e.name === moduleName)?.id)
      .filter(id => id !== undefined);

    this.hasRightForAnyModules(moduleIds, suggestionAddress)
      .pipe(
        catchError(() => {
          console.error("Error on loading the country");
          return of(null);
        })
      )
      .subscribe(hasOkStatus => {
        this.showMarkerForAddress(suggestionAddress);

        if (this.hasHouseNumber(suggestionAddress)) {
          if (hasOkStatus) {
            this.showSuggestions.set(false);
            this.lookupAddress(suggestionAddress);
            return;
          } else {
            this.interfaceStatusNotificationService.showInterfaceStatusNotification(
              InterfaceStatus.NoRight,
              suggestionAddress.address.coordinate.country
            );
          }
        }

        // Address without house number was selected -> Show Marker
        this.resetSuggestions();
        this.showSuggestions.set(false);
      });
  }

  navigateToObject(address: Address, object: TinyObject | null, type: "residential" | "commercial" | "rvm" | "mixed-real-estate") {
    this.dataModeService.resetSelectedDataMode();

    this.router.navigateByUrl("/").then(() => {
      let load$ = of(false);

      if (type === "commercial") {
        load$ = this.navigateToRealEstateObject(address, object, MODULE_NAMES.CommercialRealEstate, EstateType.Commercial);
      } else if (type === "residential") {
        load$ = this.navigateToRealEstateObject(address, object, MODULE_NAMES.ResidentialRealEstate, EstateType.Residential);
      } else if (type === "rvm") {
        load$ = this.navigateToRealEstateObject(address, object, MODULE_NAMES.Rvm, EstateType.Rvm);
      } else if (type === "mixed-real-estate") {
        load$ = this.navigateToRealEstateObject(address, object, MODULE_NAMES.MixedRealEstate, EstateType.Mixed);
      }

      load$
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          catchError(err => {
            console.error("Error navigating to object:", err);
            throw new Error(err);
          })
        )
        .subscribe();
    });
  }

  private hasRightForAnyModules(moduleIds: number[], address: SuggestionAddress) {
    return this.permissionService.hasRightForAnyModules({
      coordinate: {
        country: address.address.coordinate.country,
        latitude: address.address.coordinate.latitude,
        longitude: address.address.coordinate.longitude
      } as ObjectCoordinate,
      moduleIds: moduleIds
    });
  }

  private navigateToRealEstateObject(address: Address, object: TinyObject | null, accumateModule: AccumateModule, estateType: EstateType) {
    if (object) {
      return this.loadAndSetRealEstateObject(address, object, accumateModule, estateType);
    }

    return this.realEstateInitService.newRealEstateObject(accumateModule, estateType, address).pipe(
      filter(created => created),
      switchMap(() => this.menuService.setMode(accumateModule))
    );
  }

  private loadAndSetRealEstateObject(address: Address, object: TinyObject | null, accumateModule: AccumateModule, estateType: EstateType) {
    this.realEstateStoreService.removeObject();
    this.thematicInformationService.resetThematicInformation();

    const request: FullObjectInformationRequest = {
      address: address,
      cacheId: object?.cacheId
    };

    this.lookupResponse.set(null);
    this.showSuggestions.set(false);
    this.showAddressLookup.set(false);

    if (object?.fromAccumateCache) {
      return this.cacheService.openRealEstateFromCache(Number(object.cacheId), accumateModule);
    }

    // Check duplicates
    return this.duplicateCheckService.getShortCache(address.addressId, estateType).pipe(
      switchMap(cacheItem => {
        if (!cacheItem) {
          return of(null);
        }

        return this.duplicateCheckService.showDuplicateCheckDialog().pipe(map(loadFromCache => ({ loadFromCache, cacheItem })));
      }),
      switchMap(duplicateResponse => {
        this.loadingService.show();
        if (duplicateResponse?.loadFromCache) {
          return this.cacheService.openRealEstateFromCache(duplicateResponse.cacheItem.id, accumateModule);
        }
        return this.loadNewObjectInformationFromObjectInformation(request, object, address, estateType, accumateModule);
      }),
      tap(() => {
        if (accumateModule === MODULE_NAMES.CommercialRealEstate) {
          this.thematicInformationService.isRent.set(true);
          this.capitalizedIncomeValueService.createInternalCapitalizedIncomeValueData();
          this.capitalizedIncomeValueService.setCapitalizedIncomeValue();
        }
      }),
      switchMap(() => this.menuService.setMode(accumateModule)),
      finalize(() => this.loadingService.hide()),
      catchError(err => {
        console.error("Error loading full real estate object information:", err);
        throw err;
      })
    );
  }

  private loadNewObjectInformationFromObjectInformation(
    request: FullObjectInformationRequest,
    object: TinyObject,
    address: Address,
    estateType: EstateType,
    accumateModule: AccumateModule
  ) {
    return this.addressSearchService.getFullObjectInformation(request).pipe(
      map(fullObjectInfo => ({
        address: address,
        objectInformation: fullObjectInfo,
        customerId: object?.customerId
      })),
      switchMap(realEstateBaseObjectRequest => {
        if (accumateModule === MODULE_NAMES.MixedRealEstate) {
          this.objectInformationService.createNewObject = false;
          return this.mixedRealEstateService.loadMixedRealEstateObject(
            address,
            object,
            realEstateBaseObjectRequest.objectInformation,
            accumateModule
          );
        }

        if (object?.avmReady || accumateModule === MODULE_NAMES.CommercialRealEstate) {
          this.objectInformationService.createNewObject = false;
          return this.oldRealEstateService.loadRealEstateObject(realEstateBaseObjectRequest, estateType, accumateModule);
        } else if (accumateModule === MODULE_NAMES.Rvm) {
          return this.rvmApiService
            .updateRealEstate(realEstateBaseObjectRequest)
            .pipe(tap(rvm => this.realEstateStoreService.setRealEstate(rvm, accumateModule)));
        } else {
          return this.realEstateInitService
            .initializeNewRealEstateObject(
              accumateModule,
              estateType,
              realEstateBaseObjectRequest,
              address,
              realEstateBaseObjectRequest.objectInformation
            )
            .pipe(filter((loaded: boolean) => loaded));
        }
      })
    );
  }

  navigateToPlot(coordinate: ObjectCoordinate, tinyObject: TinyPlot | null) {
    this.dataModeService.resetSelectedDataMode();

    this.router.navigateByUrl("/").then(() => {
      if (tinyObject.fromAccumateCache) {
        this.loadingService.show();
        this.cacheService
          .openPlotFromCache(Number(tinyObject.cacheId))
          .pipe(
            takeUntilDestroyed(this.destroyRef),
            catchError(err => {
              console.error("Error navigating to plot:", err);
              throw err;
            }),
            finalize(() => {
              this.showSuggestions.set(false);
              this.showAddressLookup.set(false);
              this.loadingService.hide();
            })
          )
          .subscribe();
        return;
      }

      const plotRequest: PlotRequest = {
        Coordinate: coordinate,
        Area: tinyObject.area
      };
      this.loadingService.show();
      this.plotService
        .createOrUpdateNewPlot(plotRequest)
        .pipe(
          takeUntilDestroyed(this.destroyRef),
          switchMap(() => this.menuService.setMode(MODULE_NAMES.BuildingLand)),
          catchError(err => {
            console.error("Error navigating to plot:", err);
            throw err;
          }),
          finalize(() => {
            this.showSuggestions.set(false);
            this.showAddressLookup.set(false);
            this.loadingService.hide();
          })
        )
        .subscribe();
    });
  }

  private hasHouseNumber(address: SuggestionAddress) {
    return address.address.houseNumber !== null && address.address.houseNumber !== undefined && address.address.houseNumber !== "";
  }

  private showMarkerForAddress(suggestionAddress: SuggestionAddress) {
    if (suggestionAddress.address.coordinate.latitude && suggestionAddress.address.coordinate.longitude) {
      this.showAddressMarker(suggestionAddress.address);
      return;
    }

    this.addressSearchService.getAddressById(suggestionAddress).subscribe(() => this.showAddressMarker(suggestionAddress.address));
  }

  handleAddressOnMapClick(coordinate: Coordinate, suggestions: SuggestionAddress[] | null, country: string) {
    this.reset();
    this.suggestions.set(suggestions);
    this.lookupResponse.set(null);
    this.hasLookupError.set(false);

    this.showSuggestions.set(false);
    this.showAddressLookup.set(true);

    if (!suggestions?.length) {
      let address: SuggestionAddress;
      address.address = {
        id: 0
      };

      address.address.coordinate = getObjectCoordinateFromCoordinate(coordinate, country);
      this.lookupAddress(address);
      return;
    }

    this.showSuggestions.set(true);
    this.showAddressLookup.set(false);

    if (suggestions.length > 1) {
      this.addressMarkerService.removeAddressLayer();
      this.addressMarkerService.setAddressMarker(coordinate, MatchAccurence.Street);
    }
  }

  private goToHome() {
    this.dataModeService.resetSelectedDataMode();
    this.router.navigateByUrl("/").then();
  }

  private showAddressMarker(address: Address) {
    this.addressMarkerService.removeAddressLayer();
    const coordinate = new Coordinate([address.coordinate.longitude, address.coordinate.latitude], EPSGType.LonLat);

    this.addressMarkerService.setAddressMarker(coordinate, address.matchAccurence);
    this.mapViewService.setViewByCoordinates(coordinate.Get(EPSGType.OSM), this.basemapService.getZoomLevel());
  }

  private reset() {
    this.address.set("");
    this.resetSuggestions();
    this.selectedSuggestion.set(null);
    this.addressMarkerService.removeAddressLayer();
  }

  private resetSuggestions() {
    this.suggestions.set([]);
    this.showSuggestions.set(false);
  }
}
